/**
 * pages/CreateFormTemplate.tsx
 *
 * Admin-only form template builder. Fields are created via CSV import or
 * manually added/edited inline in NeedsListEditor. No catalog sidebar.
 * System email field is injected as a hardcoded locked NeedItem on new forms.
 *
 * Updated Jun 2026: removed catalog sidebar (CatalogSidebar, CustomDocumentModal,
 * CustomInformationModal, useDocumentCatalog, useInformationCatalog).
 * Admin-only guard added. System email field no longer depends on catalog lookup.
 * convertRowsToNeeds called without catalog options.
 * Removed FormLogoDisplay (not relevant to admin form builder workflow).
 */

import { useState, useRef, useEffect, useCallback, FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import Button from '../components/ui/Button';
import { ArrowLeft, Save, Upload } from 'lucide-react';
import NeedsListEditor from '../components/needs/NeedsListEditor';
import { UnsavedChangesModal } from '../components/ui/UnsavedChangesModal';
import { SmartTableImportModal } from '../components/forms/SmartTableImportModal';
import type { NeedItem } from '../types/needs';
import { convertRowsToNeeds } from '../utils/smartTableConverter';
import type { SmartTableRow } from '../utils/smartTableParser';
import { useFormTemplateSave } from '../hooks/useFormTemplateSave';
import { useFormTemplateLoader } from '../hooks/useFormTemplateLoader';
import { detectOrderConflicts, findFirstOrderConflict, topologicalSortNeeds } from '../utils/conditionOrderUtils';

const SYSTEM_EMAIL_LABEL = 'Email';

function makeSystemEmailNeed(): NeedItem {
  return {
    id: crypto.randomUUID(),
    type: 'short_text',
    label: SYSTEM_EMAIL_LABEL,
    description: null,
    is_required: true,
    position: 0,
    conditions: null,
    section: null,
    is_system: true,
  };
}

export default function CreateFormTemplate() {
  const { user, userProfile } = useAuth();
  const isUserAdmin = userProfile?.isAdmin ?? false;
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { draftId, id } = useParams<{ draftId?: string; id?: string }>();
  const templateId = draftId || id;

  const [isReady, setIsReady] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const hasInitialised = useRef(false);
  const hasInitialisedSystemField = useRef(false);
  const needsRef = useRef<NeedItem[]>([]);

  const handleLoadComplete = useCallback(() => setHasUnsavedChanges(false), []);

  const {
    loadingTemplate,
    name, setName,
    needs, setNeeds,
    isEditingActive,
  } = useFormTemplateLoader({
    templateId,
    userId: user?.id,
    userProfileLoaded: !!userProfile,
    isUserAdmin,
    navigate,
    showToast,
    onLoadComplete: handleLoadComplete,
  });

  const { saving, saveFormTemplate } = useFormTemplateSave({
    templateId,
    userId: user?.id ?? '',
    isUserAdmin,
    name,
    needs,
    showToast,
    onSaved: () => setHasUnsavedChanges(false),
  });

  // Admin-only guard
  useEffect(() => {
    if (!userProfile) return;
    if (!userProfile.isAdmin) {
      navigate('/my-forms');
    }
  }, [userProfile, navigate]);

  useEffect(() => {
    if (hasInitialised.current || !userProfile) return;
    hasInitialised.current = true;
    setIsReady(true);
  }, [userProfile]);

  useEffect(() => { needsRef.current = needs; }, [needs]);

  // Inject system email field for new forms only
  useEffect(() => {
    if (templateId || !isReady || hasInitialisedSystemField.current) return;
    hasInitialisedSystemField.current = true;
    setNeeds([makeSystemEmailNeed()]);
  }, [templateId, isReady, setNeeds]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const handleImport = (rows: SmartTableRow[]) => {
    const { needs: generated, skippedCount, failedConditionsCount, tooManyConditionsCount } = convertRowsToNeeds(
      rows,
      needsRef.current,
    );
    if (generated.length > 0) {
      setNeeds((prev) => {
        const offset = prev.length;
        const rebased = generated.map((n, i) => ({ ...n, position: offset + i }));
        const merged = [...prev, ...rebased];
        return detectOrderConflicts(merged) ? topologicalSortNeeds(merged) : merged;
      });
      setHasUnsavedChanges(true);
    }
    setShowImportModal(false);
    if (tooManyConditionsCount > 0) {
      showToast(
        `${tooManyConditionsCount} condition${tooManyConditionsCount > 1 ? 's' : ''} ignorée${tooManyConditionsCount > 1 ? 's' : ''} - le maximum est 3 critères par champ`,
        'warning'
      );
    }
    if (failedConditionsCount > 0) {
      showToast(
        failedConditionsCount === 1
          ? `1 condition n'a pas pu être résolue et a été ignorée`
          : `${failedConditionsCount} conditions n'ont pas pu être résolues et ont été ignorées`,
        'warning'
      );
    }
    if (generated.length === 0 && skippedCount === 0) {
      showToast(`Aucun élément trouvé à importer`, 'info');
      return;
    }
    if (skippedCount > 0) {
      showToast(`${generated.length} élément${generated.length > 1 ? 's' : ''} importé${generated.length > 1 ? 's' : ''} (${skippedCount} ligne${skippedCount > 1 ? 's' : ''} ignorée${skippedCount > 1 ? 's' : ''})`, 'info');
    } else {
      showToast(`${generated.length} élément${generated.length > 1 ? 's' : ''} importé${generated.length > 1 ? 's' : ''} dans le formulaire`, 'success');
    }
  };

  const handleNeedsChange = (updatedNeeds: NeedItem[]) => {
    if (detectOrderConflicts(updatedNeeds)) {
      const sorted = topologicalSortNeeds(updatedNeeds);
      const orderChanged = sorted.some((n, i) => n.id !== updatedNeeds[i].id);
      setNeeds(sorted);
      if (orderChanged) {
        const conflict = findFirstOrderConflict(updatedNeeds);
        showToast(conflict ? `Ordre ajusté - « ${conflict.conditionalLabel} » repositionné après « ${conflict.sourceLabel} »` : 'Ordre ajusté - questions conditionnelles repositionnées', 'info');
      }
    } else {
      setNeeds(updatedNeeds);
    }
    setHasUnsavedChanges(true);
  };

  const handleSaveAsDraft = async () => {
    const success = await saveFormTemplate('draft', false);
    if (success) { showToast('Brouillon sauvegardé', 'success'); navigate('/my-forms'); }
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    const success = await saveFormTemplate('active', true);
    if (success) { showToast('Formulaire créé avec succès', 'success'); navigate('/my-forms'); }
  };

  const handleBack = () => {
    if (hasUnsavedChanges) { setShowUnsavedModal(true); return; }
    navigate('/my-forms');
  };

  const handleDiscard = () => {
    setHasUnsavedChanges(false);
    setShowUnsavedModal(false);
    setNeeds([]);
    navigate('/my-forms');
  };

  if (!isReady || loadingTemplate) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4" />
          <p className="text-neutral-600">Chargement...</p>
        </div>
      </div>
    );
  }

  const pageTitle = templateId
    ? (isEditingActive ? 'Modifier le formulaire' : 'Continuer le formulaire')
    : 'Nouveau formulaire';

  return (
    <div className="h-screen bg-neutral-50 flex flex-col">
      <nav className="bg-white shadow-soft flex-shrink-0 border-b border-neutral-200">
        <div className="max-w-full mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button onClick={handleBack} aria-label="Retour" className="p-2 hover:bg-neutral-100 rounded-lg transition-colors" title="Retour">
              <ArrowLeft className="w-5 h-5 text-neutral-600" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-neutral-900">{pageTitle}</h1>
              <p className="text-sm text-neutral-500">Créez votre formulaire par import CSV ou ajout manuel</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="secondary" onClick={() => setShowImportModal(true)} title="Importer des champs depuis un fichier CSV ou Excel">
              <Upload className="w-4 h-4 mr-2" />Importer CSV / Excel
            </Button>
            <Button variant="secondary" onClick={handleSaveAsDraft} disabled={saving || !name.trim()}>
              <Save className="w-4 h-4 mr-2" />Enregistrer comme brouillon
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Enregistrement...' : (isEditingActive ? 'Enregistrer les modifications' : 'Créer le formulaire')}
            </Button>
          </div>
        </div>
      </nav>

      <main className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto p-8 space-y-8">
          <div className="bg-white rounded-2xl p-6 shadow-soft border border-neutral-200 space-y-3">
            <input
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); setHasUnsavedChanges(true); }}
              placeholder="Nom du formulaire..."
              className="w-full text-xl font-semibold text-neutral-900 placeholder:text-neutral-400 border border-transparent bg-transparent focus:border-primary-300 focus:bg-white focus:ring-2 focus:ring-primary-600 focus:outline-none px-3 py-2 -mx-3 rounded-xl transition-colors"
              required
            />
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-soft border border-neutral-200">
            <h2 className="text-lg font-semibold text-neutral-900 mb-4">Liste des éléments</h2>
            <NeedsListEditor needs={needs} onNeedsChange={handleNeedsChange} showToast={showToast} />
          </div>
        </div>
      </main>

      {showImportModal && (
        <SmartTableImportModal onImport={handleImport} onClose={() => setShowImportModal(false)} />
      )}
      <UnsavedChangesModal isOpen={showUnsavedModal} onClose={() => setShowUnsavedModal(false)} onSaveDraft={handleSaveAsDraft} onDiscard={handleDiscard} isSaving={saving} canSaveDraft={name.trim().length > 0} />
    </div>
  );
}
